export function getStateOfSalesL1LoadState({
  forecastAvailable,
  modelError,
  modelLoading,
}: {
  forecastAvailable: boolean;
  modelError: unknown;
  modelLoading: boolean;
}) {
  if (modelLoading) return "loading" as const;
  if (modelError || !forecastAvailable) return "error" as const;
  return "ready" as const;
}

export type StateOfSalesForecastSource =
  | "currencies"
  | "deals"
  | "movement"
  | "pipelines"
  | "prior_year_actual"
  | "stage_times"
  | "teams";

type StateOfSalesForecastSourceState = {
  error: unknown;
  isLoading: boolean;
};

export function classifyStateOfSalesForecastSources({
  requireMovement,
  requireTeams,
  sources,
}: {
  requireMovement: boolean;
  requireTeams: boolean;
  sources: Record<StateOfSalesForecastSource, StateOfSalesForecastSourceState>;
}) {
  const requiredSources = new Set<StateOfSalesForecastSource>([
    "currencies",
    "deals",
    "pipelines",
    ...(requireMovement ? ["movement" as const] : []),
    ...(requireTeams ? ["teams" as const] : []),
  ]);
  const entries = Object.entries(sources) as Array<[
    StateOfSalesForecastSource,
    StateOfSalesForecastSourceState,
  ]>;
  const requiredEntries = entries.filter(([source]) => requiredSources.has(source));
  const optionalEntries = entries.filter(([source]) => !requiredSources.has(source));
  const requiredErrorSources = requiredEntries
    .filter(([, state]) => Boolean(state.error))
    .map(([source]) => source);
  const optionalErrorSources = optionalEntries
    .filter(([, state]) => Boolean(state.error))
    .map(([source]) => source);

  return {
    optionalError: optionalEntries.find(([, state]) => state.error)?.[1].error ?? null,
    optionalErrorSources,
    optionalLoading: optionalEntries.some(([, state]) => state.isLoading),
    requiredError: requiredEntries.find(([, state]) => state.error)?.[1].error ?? null,
    requiredErrorSources,
    requiredLoading: requiredEntries.some(([, state]) => state.isLoading),
  };
}
